"use client";

import { useRef, useState, type ComponentType, type ReactNode, type SVGProps } from "react";
import { motion } from "framer-motion";
import { gsap, ScrollTrigger, useGSAP } from "@/lib/gsap";
import { reduceMotion } from "@/lib/motion";
import { useLenis } from "@/hooks/useLenis";
import { ChartBars, CheckSquare, Receipt, Target, User } from "@/components/ui/icons";
import { TOUR } from "@/content/quikit";
import { cn } from "@/lib/utils";
import { SectionIntro } from "./SectionIntro";
import { StoryPanel } from "./story/StoryPanel";
import { CrmMock, FinanceMock, InsightsMock, PeopleMock, ProjectsMock, TourBody } from "./story/panels";

const VISUALS: Record<string, { icon: ComponentType<SVGProps<SVGSVGElement>>; mock: ReactNode }> = {
  crm: { icon: Target, mock: <CrmMock /> },
  projects: { icon: CheckSquare, mock: <ProjectsMock /> },
  people: { icon: User, mock: <PeopleMock /> },
  finance: { icon: Receipt, mock: <FinanceMock /> },
  insights: { icon: ChartBars, mock: <InsightsMock /> },
};

/**
 * Product tour as a scrollytelling chapter: a sticky rail on the left tracks which app
 * panel is under the reading line; the marker glides between items and clicking one
 * glides the page (Lenis) to its panel.
 */
export function FeatureStory() {
  const root = useRef<HTMLElement>(null);
  const [active, setActive] = useState(0);
  const lenis = useLenis();

  useGSAP(
    () => {
      const el = root.current;
      if (!el) return;
      gsap.utils.toArray<HTMLElement>("[data-story-panel]", el).forEach((panel, i) => {
        ScrollTrigger.create({
          trigger: panel,
          start: "top 55%",
          end: "bottom 55%",
          onToggle: (self) => {
            if (self.isActive) setActive(i);
          },
        });
      });
      if (reduceMotion()) return;
      gsap.fromTo(
        el.querySelector("[data-rail-progress]"),
        { scaleY: 0 },
        {
          scaleY: 1,
          ease: "none",
          scrollTrigger: { trigger: el.querySelector("[data-story-panels]"), start: "top 55%", end: "bottom 55%", scrub: true },
        },
      );
    },
    { scope: root },
  );

  return (
    <section id="tour" ref={root} aria-labelledby="tour-title" className="pt-[clamp(120px,12vw,190px)]">
      <div className="wrap">
        <SectionIntro
          eyebrow="Product tour"
          title="See how the apps work."
          titleId="tour-title"
          text="Real workflows, with Quik Intelligence built in. Pick an app to take a look."
        />
      </div>
      <div className="wrap mt-14 grid gap-10 md:mt-16 md:grid-cols-[minmax(0,180px)_1fr] md:gap-[clamp(24px,4vw,56px)] lg:grid-cols-[minmax(0,210px)_1fr]">
        <aside className="relative hidden md:block">
          <span aria-hidden="true" className="absolute bottom-0 left-0 top-2 w-px bg-line" />
          <span aria-hidden="true" data-rail-progress="" className="absolute bottom-0 left-0 top-2 w-px origin-top bg-ink/20" />
          <nav aria-label="Product tour" className="sticky top-[calc(var(--nav-h)+44px)]">
            <span aria-hidden="true" className="absolute -left-[2px] top-[7px] size-[5px] rounded-full bg-ink" />
            <ul className="space-y-[14px] pl-5">
              {TOUR.map((app, i) => {
                const isActive = i === active;
                return (
                  <li key={app.key} className="relative">
                    {isActive && (
                      <motion.span
                        layoutId="rail-marker"
                        aria-hidden="true"
                        className="absolute -left-5 top-1/2 h-[18px] w-[2px] -translate-y-1/2 rounded-full bg-ink"
                        transition={{ type: "spring", duration: 0.55, bounce: 0.12 }}
                      />
                    )}
                    <button
                      type="button"
                      aria-current={isActive ? "step" : undefined}
                      onClick={() => lenis?.scrollTo(`#story-${app.key}`, { offset: -110 })}
                      className={cn(
                        "text-left text-[14px] leading-snug tracking-[-0.01em] transition-colors duration-300",
                        isActive ? "text-ink" : "text-faint hover:text-muted",
                      )}
                    >
                      {app.name}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>

        <div data-story-panels="" className="space-y-[clamp(56px,6vw,96px)]">
          {TOUR.map((app) => {
            const v = VISUALS[app.key];
            return (
              <StoryPanel key={app.key} id={`story-${app.key}`} icon={v.icon} kicker={`${app.name} · ${app.category}`} title={app.headline}>
                <TourBody app={app} mock={v.mock} />
              </StoryPanel>
            );
          })}
        </div>
      </div>
    </section>
  );
}
